import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Radio,
    RadioGroup,
    FormControlLabel,
    TextField,
    Typography,
    Box,
    CircularProgress,
    Link,
    Grid
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../../utils/languageContext';
import { encryptTextApi, fetchAbdmAccessToken, useResendOTP, processAbhaProfileData, formatAbhaInput } from '../../../../helpers/abdm-helper';
import { abdmAPI } from '../../../../services/abdm.api';
import { toast } from "react-toastify";
import AbhaProfileModal from './AbhaProfileModal/AbhaProfileModal';
import AbhaCreateModal from './AbhaCreateModal/AbhaCreateModal';
import Step4AbhaRegistered from './AbhaCreateModal/Step4AbhaRegistered/Step4AbhaRegistered';
import DemographicAuth from './DemographicAuth/DemographicAuth';
import "./Abdm.css";

const AbdmSection = (props) => {
    const { setAddbenificeryModal, openScannerModal, addbenificeryModal } = props;
    const { t } = useLanguage();
    const navigate = useNavigate();


    const [isLoading, setIsLoading] = useState(false);
    const [openAbha, setOpenAbha] = useState(false);
    const [searchBy, setSearchBy] = useState("mobile");
    const [abhaId, setAbhaId] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");
    const [otp, setOtp] = useState("");
    const [step, setStep] = useState(1);
    const [openProfile, setOpenProfile] = useState(false);
    const [abdmAccessToken, setAbdmAccessToken] = useState(null);
    const [searchTxnId, setSearchTxnId] = useState(null);
    const [otpTxnId, setOtpTxnId] = useState(null);
    const [failedTxnId, setFailedTxnId] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [abhaData, setAbhaData] = useState(null);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [selectedAuthType, setSelectedAuthType] = useState("MOBILE_OTP");
    const [abhaAuthType, setAbhaAuthType] = useState("aadhaar-verify");
    const [encryptedAbhaId, setEncryptedAbhaId] = useState(null);
    const [encryptedMobileIndex, setEncryptedMobileIndex] = useState(null);

    const {
        resendCount,
        canResend,
        formattedTime,
        handleResend,
        resetTimer,
        startTimer,
        maxResendCount
    } = useResendOTP({
        maxResendCount: 3,
        countdownSeconds: 60,
        autoStart: false,
        onResend: async () => {
            try {
                setIsLoading(true);
                searchBy === "mobile" ? await resendMobileOtp() : await resendAbhaOtp();
                toast.success("OTP resent successfully");
            } catch (error) {
                toast.error(error?.response?.data?.message || "Resend failed");
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
    });

    useEffect(() => {
        if (addbenificeryModal) loadAbdmToken();
        return () => { if (!addbenificeryModal) resetState(); };
    }, [addbenificeryModal]);

    const loadAbdmToken = async () => {
        const token = await fetchAbdmAccessToken(setIsLoading, toast);
        if (token) setAbdmAccessToken(token);
    };

    const resetState = () => {
        setSearchBy("mobile");
        setAbhaId("");
        setMobileNumber("");
        setOtp("");
        setStep(1);
        setSearchTxnId(null);
        setOtpTxnId(null);
        setAccounts([]);
        setAbhaData(null);
        resetTimer();
    };

    const handleRegisterBeneficiary = () => {
        setAddbenificeryModal(false);
        if (props.redirectUrl) {
            navigate(props.redirectUrl);
        } else {
            navigate("/detailsabha");
        }
    };

    const handleSearchByMobile = async () => {
        if (!mobileNumber.trim()) {
            toast.error("Please enter mobile number");
            return;
        }
        try {
            setIsLoading(true);
            const encryptedMobile = await encryptTextApi(mobileNumber.trim());
            const response = await abdmAPI.searchByMobile(
                { mobile: encryptedMobile },
                { headers: { accessToken: abdmAccessToken } }
            );
            if (response?.success) {
                const data = response?.data?.[0];
                setSearchTxnId(data?.txnId);
                if (data?.ABHA?.length > 0) {
                    setAccounts(data?.ABHA);
                    setOpenProfile(true);
                } else {
                    toast.info("No ABHA profiles found");
                }
            }
        } catch (error) {
            toast.error(error?.response?.data?.message);
        } finally {
            setIsLoading(false);
        }
    };

    const sendMobileOtp = async (profile, authType) => {
        let encryptedIndex = await encryptTextApi(profile.index.toString());
        setEncryptedMobileIndex(encryptedIndex);
        const response = await abdmAPI.sendMobileOtp(
            { loginId: encryptedIndex, type: authType, txnId: searchTxnId },
            { headers: { accessToken: abdmAccessToken } }
        );
        if (response?.success) {
            setOtpTxnId(response?.data?.txnId);
            return response?.data;
        }
        throw new Error("Failed to send OTP");
    };

    const resendMobileOtp = async () => {
        const txnIdToUse = failedTxnId || searchTxnId;
        const response = await abdmAPI.sendMobileOtp(
            { loginId: encryptedMobileIndex, type: selectedAuthType, txnId: txnIdToUse },
            { headers: { accessToken: abdmAccessToken } }
        );

        if (response?.success) {
            setOtpTxnId(response?.data?.txnId);
            return response?.data;
        }
    };

    const sendAbhaOtp = async () => {
        // Use stored encrypted value or encrypt for first time
        let encryptedValue = encryptedAbhaId;

        if (!encryptedValue) {
            encryptedValue = await encryptTextApi(abhaId.trim());
            setEncryptedAbhaId(encryptedValue);
        }
        const response = await abdmAPI.sendAbhaOtp(
            { loginId: encryptedValue, type: abhaAuthType },
            { headers: { accessToken: abdmAccessToken } }
        );
        if (response?.success) {
            setOtpTxnId(response?.data?.txnId);
            return response?.data;
        }
        throw new Error("Failed to send OTP");
    };

    const resendAbhaOtp = async () => {
        const response = await abdmAPI.sendAbhaOtp(
            { loginId: encryptedAbhaId, type: abhaAuthType },
            { headers: { accessToken: abdmAccessToken } }
        );
        if (response?.success) {
            setOtpTxnId(response?.data?.txnId);
            return response?.data;
        }
    };

    const handleDemographicAuth = async (payload) => {
        try {
            setIsLoading(true);
            const encryptedAadhaar = await encryptTextApi(payload.aadhaarNumber);

            const requestPayload = {
                ...payload,
                aadhaarNumber: encryptedAadhaar
            };

            const response = await abdmAPI.userDemographicVerify(
                requestPayload,
                { headers: { accessToken: abdmAccessToken } }
            );

            if (response?.success) {
                // Map demographic auth response keys to standard keys expected by processAbhaProfileData
                const data = {
                    ...response?.data,
                    ABHANumber: response?.data?.ABHANumber || response?.data?.healthIdNumber,
                    preferredAbhaAddress: response?.data?.preferredAbhaAddress || response?.data?.healthId
                };
                setAbhaData(data);
                processAbhaProfileData(data);
                setStep(4);
            } else {
                throw new Error(response?.message || "Demographic authentication failed");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "Demographic authentication failed");
        } finally {
            setIsLoading(false);
        }
    };

    const handleMainAction = async () => {
        if (searchBy === "mobile") {
            await handleSearchByMobile();
        } else {
            try {
                setIsLoading(true);
                await sendAbhaOtp();
                setStep(2);
                startTimer();
            } catch (e) { toast.error(e.message); }
            finally { setIsLoading(false); }
        }
    };

    const handleProfileNext = async (profile, authType) => {
        try {
            setIsLoading(true);
            setSelectedProfile(profile);
            setSelectedAuthType(authType);
            await sendMobileOtp(profile, authType);
            setOpenProfile(false);
            setStep(2);
            startTimer();
        } catch (error) {
            toast.error(error?.response?.data?.message);
        } finally { setIsLoading(false); }
    };

    const handleVerifyOtp = async () => {
        try {
            setIsLoading(true);
            const encryptedOtp = await encryptTextApi(otp);
            const response = searchBy === "mobile"
                ? await abdmAPI.verifyMobileOtp({ type: selectedAuthType, txnId: otpTxnId, otpValue: encryptedOtp }, { headers: { accessToken: abdmAccessToken } })
                : await abdmAPI.verifyAbhaOtp({ type: abhaAuthType, txnId: otpTxnId, otpValue: encryptedOtp }, { headers: { accessToken: abdmAccessToken } });

            const data = response.data;
            if (data?.authResult === "success") {
                if (data?.accounts?.[0]) {
                    await verifyAbhaUser(data.accounts[0], data.token);
                }
            } else {
                setOtpTxnId(data.txnId);
                if (searchBy === "mobile") setFailedTxnId(data.txnId);
                toast.error("Invalid OTP");
            }
        } catch (error) {
            toast.error("Verification failed");
        } finally { setIsLoading(false); }
    };

    const verifyAbhaUser = async (profile, authToken) => {
        const response = await abdmAPI.verifyAbhaOtpUser(
            { token: authToken },
            { headers: { accessToken: abdmAccessToken } }
        );
        if (response?.success) {
            setAbhaData(response?.data);
            // Process and store ABHA profile data
            processAbhaProfileData(response?.data);
            setStep(4);
        }
    };

    const formatAbhaId = (value) => {
        const digits = value.replace(/\D/g, "");
        const parts = digits.match(/.{1,4}/g) || [];
        return parts.join("-").slice(0, 17);
    };

    const isInputValid = searchBy === "mobile"
        ? /^[6-9]\d{9}$/.test(mobileNumber)
        : abhaId.replace(/\D/g, "").length === 14;

    const renderActionButtons = (isValid, mainAction, btnText = "Next") => (
        <>
            <div className="btnSec">
                <Button
                    onClick={() => setOpenAbha(true)}
                    sx={{ textTransform: 'none', mb: 2, color: '#3fc4f4', fontWeight: 600 }}
                >
                    Create ABHA
                </Button>
            </div>

            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    fullWidth
                    variant="contained"
                    disabled={!isValid || isLoading}
                    onClick={mainAction}
                    sx={{
                        borderRadius: '25px',
                        py: 1.5,
                        background: isValid ? "linear-gradient(90deg, #3FC4F4, #00AEEF)" : "#ccc",
                        boxShadow: 'none'
                    }}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : btnText}
                </Button>
                <Button
                    variant="outlined"
                    onClick={resetState}
                    sx={{ borderRadius: '25px', textTransform: 'none', px: 3 }}
                >
                    Reset
                </Button>
            </Box>
            <div className="d-none11">

                <Box sx={{ position: 'relative', textAlign: 'center', my: 3 }}>
                    <Typography variant="body2" sx={{ background: '#fff', px: 1, position: 'relative', zIndex: 1, display: 'inline-block' }}>OR</Typography>
                    <Box sx={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: '#eee' }} />
                </Box>

                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<QrCodeScannerIcon />}
                    onClick={openScannerModal}
                    sx={{ borderRadius: '25px', py: 1, color: '#666', borderColor: '#ddd', textTransform: 'none' }}
                >
                    Scan ABHA QR Code
                </Button>
            </div>
        </>
    );

    return (
        <>
            <Dialog
                open={addbenificeryModal}
                onClose={() => setAddbenificeryModal(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: "25px", p: 1 } }}
            >
                <DialogTitle>
                    <IconButton
                        onClick={() => setAddbenificeryModal(false)}
                        sx={{ position: "absolute", right: 16, top: 16 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ px: 4, pb: 4 }}>
                    <Grid
                        container
                        spacing={2}
                        sx={{
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Grid
                            size={5}
                            sx={{
                                textAlign: "center"
                            }}
                        >
                            <img src="/images/Abha.svg" alt="ABHA" className='abha_logo' />
                            <Typography variant="h5" fontWeight={700}>
                                {t("dashboard.add_beneficiary_button")}
                            </Typography>
                        </Grid>
                        {step === 1 && (
                            <Grid size={7}>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    Search your ABHA using:
                                </Typography>
                                <RadioGroup
                                    row
                                    value={searchBy}
                                    onChange={(e) => setSearchBy(e.target.value)}
                                    sx={{ mb: 2 }}
                                >
                                    <FormControlLabel value="mobile" control={<Radio size="small" />} label="Mobile No." />
                                    <FormControlLabel value="abha" control={<Radio size="small" />} label="ABHA Number" />
                                    <FormControlLabel value="demographic" control={<Radio size="small" />} label="Demographic Authentication" />
                                </RadioGroup>

                                {searchBy === "mobile" ? (
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Enter 10 digit mobile no."
                                        value={mobileNumber}
                                        onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                        sx={{ mb: 2 }}
                                    />
                                ) : searchBy === "abha" ? (
                                    <>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            placeholder="XX-XXXX-XXXX-XXXX"
                                            value={abhaId}
                                            onChange={(e) => {
                                                const formatted = formatAbhaInput(e.target.value);
                                                setAbhaId(formatted);
                                            }}
                                            sx={{ mb: 2 }}
                                        />
                                        <FormControl fullWidth sx={{ mb: 1 }}>
                                            <InputLabel>Auth Type</InputLabel>
                                            <Select
                                                value={abhaAuthType}
                                                label="Auth Type"
                                                onChange={(e) => setAbhaAuthType(e.target.value)}
                                            >
                                                <MenuItem value="aadhaar-verify">Aadhaar OTP</MenuItem>
                                                <MenuItem value="mobile-verify">Mobile OTP</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </>
                                ) : (
                                    <DemographicAuth 
                                        onSubmit={handleDemographicAuth} 
                                        isLoading={isLoading} 
                                        renderActionButtons={(isValid, submitAction) => renderActionButtons(isValid, submitAction, "Authenticate")}
                                    />
                                )}
                                
                                {searchBy !== "demographic" && renderActionButtons(isInputValid, handleMainAction)}
                            </Grid>
                        )}

                        {step === 2 && (
                            <Grid size={7}>
                                <Typography variant="h5" fontWeight={700} gutterBottom>OTP Verification</Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                    Enter OTP sent to your {searchBy === "mobile" ? "Mobile Number" : "Aadhaar/Mobile"}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-start', mb: 3 }}>
                                    {[0, 1, 2, 3, 4, 5].map((index) => (
                                        <TextField
                                            key={index}
                                            className="otp-input"
                                            inputRef={(el) => {
                                                if (!window.otpRefs) window.otpRefs = [];
                                                window.otpRefs[index] = el;
                                            }}
                                            inputProps={{
                                                style: { textAlign: 'center', fontSize: '24px', padding: '14px', height: "1em" },
                                                maxLength: 1
                                            }}
                                            value={otp[index] || ''}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, "");
                                                if (value) {
                                                    const newOtp = otp.split('');
                                                    newOtp[index] = value;
                                                    setOtp(newOtp.join(''));
                                                    // Auto-focus next box
                                                    if (index < 5 && window.otpRefs[index + 1]) {
                                                        window.otpRefs[index + 1].focus();
                                                    }
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Backspace') {
                                                    const newOtp = otp.split('');
                                                    if (newOtp[index]) {
                                                        // Delete current digit
                                                        newOtp[index] = '';
                                                        setOtp(newOtp.join(''));
                                                    } else if (index > 0 && window.otpRefs[index - 1]) {
                                                        // Move to previous box if current is empty
                                                        window.otpRefs[index - 1].focus();
                                                    }
                                                }
                                            }}
                                            sx={{
                                                width: '50px',
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px'
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleVerifyOtp}
                                    disabled={otp.length !== 6 || isLoading}
                                    sx={{ borderRadius: '25px', py: 1.5, mb: 2, background: "#3fc4f4" }}
                                >
                                    {isLoading ? <CircularProgress size={24} color="inherit" /> : "Verify OTP"}
                                </Button>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" color="textSecondary">Didn't receive OTP?</Typography>
                                    <Link
                                        component="button"
                                        variant="caption"
                                        onClick={handleResend}
                                        disabled={!canResend}
                                        sx={{ fontWeight: 600, color: canResend ? '#3fc4f4' : '#ccc', textDecoration: 'none' }}
                                    >
                                        Resend ({resendCount}/{maxResendCount})
                                    </Link>
                                    <Typography variant="caption" color="textSecondary">{formattedTime} remaining</Typography>
                                </Box>
                            </Grid>
                        )}

                        {step === 4 && abhaData && (
                            <Grid size={7}>
                                <Step4AbhaRegistered
                                    abhaData={abhaData}
                                    isNeeded={false}
                                    onRegisterBeneficiary={handleRegisterBeneficiary}
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
            </Dialog>

            {/* ABHA Create Modal */}
            <AbhaCreateModal open={openAbha} onClose={() => setOpenAbha(false)} redirectUrl={props.redirectUrl} />

            <AbhaProfileModal
                open={openProfile}
                onClose={() => setOpenProfile(false)}
                profiles={accounts}
                onNext={handleProfileNext}
            />
        </>
    );
}

export default AbdmSection;